<template>
  <div v-if="!isLoading" class="betTokensContainer">
    <template v-for="item in tokenListData" :key="item.id">
      <div class="tokenBlock">
        <div class="tokenBlockContainer">
          <img :src="getIcon(0, item.tokenAddress, true)" alt="" />
          <p class="amount" :class="!item.amount ? 'redAmount' : null">{{ formatAmountFloor(item.amount) }}</p>

          <template v-if="!item.amount">
            <el-tooltip placement="top" popper-class="popper-default" :hide-after="0">
              <template #content>
                <div class="flipCoinTooltipPopper" :class="{ 'ru-locale': $i18n.locale === 'ru' }">
                  {{ $t('flipCoinNotEnoughTokensTooltip', { amount: item.minAmount }) }}
                </div>
              </template>
              <div class="selectButtonWrapper">
                <TavernFlipCoinBigButton
                  class="selectButton"
                  :label="$t('coreSelect')"
                  :disabled="true"
                  @click="() => handleSelectButton(item)"
                />
              </div>
            </el-tooltip>
          </template>

          <template v-else>
            <div class="selectButtonWrapper">
              <TavernFlipCoinBigButton
                class="selectButton"
                :label="$t('coreSelect')"
                :disabled="false"
                @click="() => handleSelectButton(item)"
              />
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { Erc20Token__factory, Multicall__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { IFlipCoinTokenConfig } from '~/types/apiService';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';

const { exchangeTokenCallback } = defineProps<{
  exchangeTokenCallback: (token: IFlipCoinTokenConfig) => void;
}>();

const { getProvider } = useProvider();
const { blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();
const isLoading = ref(true);
const { data: tokenList } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');
const tokenListData = ref(tokenList.value || []);

watch(
  [address, tokenList],
  async () => {
    if (!tokenListData.value || !address.value) {
      isLoading.value = false;
      return;
    }

    const balanceOfCalldata = Erc20Token__factory.createInterface().encodeFunctionData('balanceOf', [address.value!]);
    const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
    const tokenAddresses = tokenListData.value.map((token: IFlipCoinTokenConfig) => [
      token.tokenAddress,
      balanceOfCalldata
    ]);
    const multicallResult = await multicallContract?.tryAggregate.staticCall(
      true,
      tokenAddresses.map((item) => ({
        target: item[0],
        callData: item[1]
      }))
    );

    const balances = multicallResult.map((elem: any) => {
      if (elem[1].length < 5) return;

      return Number(BigNumber(formatEther(elem[1])).decimalPlaces(2, BigNumber.ROUND_DOWN));
    });

    tokenListData.value = tokenListData.value?.map((token: any, index: number) => ({
      ...token,
      amount: balances[index] || 0
    }));

    isLoading.value = false;
  },
  { immediate: true }
);

const handleSelectButton = (token: IFlipCoinTokenConfig) => {
  exchangeTokenCallback(token);
};
</script>

<style lang="scss">
.el-popper {
  .flipCoinTooltipPopper {
    font-size: 20px;
    line-height: 24px;
    max-width: 300px;
    padding: 15px 6px;
    text-align: center;
  }
}
</style>

<style lang="scss" scoped>
.betTokensContainer {
  width: 100%;
  color: white;
  grid-row-gap: 0;
  grid-column-gap: 30px;
  grid-template-rows: auto auto;
  grid-template-columns: repeat(5, 1fr);
  grid-auto-columns: 1fr;
  display: grid;
  height: 420px;
  overflow-y: scroll;

  @media screen and (max-width: 479px) {
    grid-template-columns: repeat(2, 1fr);
    scrollbar-width: none;
    grid-column-gap: unset;
    gap: 10px;
    height: 100%;
    justify-items: center;
  }
}

.tokenBlock {
  width: 135px;
  height: 159px;
  background: linear-gradient(to bottom, #4e3527, #000701);
  border-radius: 12px;
  border: 1px solid #483b20;
  margin-bottom: 15px;

  @media screen and (max-width: 479px) {
    margin-bottom: 0;
    width: 100%;
    height: max-content;
  }

  .selectButtonWrapper {
    width: 100%;
    margin-top: -4px;
  }

  .selectButton {
    width: 100%;
    max-width: 100%;
    height: 40px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    border: unset;
    padding: 0;
    background: linear-gradient(to bottom, #f7ab06, #591500);
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      transform: unset;
    }

    &:active {
      transform: unset;
    }
  }

  .tokenBlockContainer {
    display: flex;
    flex-direction: column;
    align-items: center;

    .amount {
      font-size: 24px;
      padding: 0 0 12px;
      font-weight: 600;
      color: #fff;
    }

    .redAmount {
      color: red;
    }

    img {
      width: 75px;
      height: 75px;
    }
  }
}
</style>
